import React, { useState } from 'react';

import { MANUAL_TRANSITIONS, BED_STATE_META } from '../../../constants/occupancy';

const labelFor = (code) => BED_STATE_META[code]?.label || code;

/**
 * BedPopover (C2 / FT8) — the per-bed detail + manual-action surface.
 *
 * - Detail fields come from the board payload (no per-bed fetch).
 * - The action menu is derived from the S5 manual-transition matrix for the
 *   bed's CURRENT state; OCCUPIED / RESERVED expose NO board action (freed via
 *   the admission workflow). The whole menu is HIDDEN without MANAGE_BED_STATE.
 * - Block requires a reason: the "Block bed" action reveals a reason field and
 *   the confirm button stays DISABLED until it is non-empty (mirrors the backend
 *   BLOCK_REASON_REQUIRED guard — a blocked bed always carries a reason).
 * - Optimistic → the page applies the target locally and calls `onAction`; on a
 *   409 BED_STATE_CONFLICT the popover surfaces a fresh-state toast (the store
 *   has already self-corrected the chip from the server truth / WS frame).
 * - The card clamps to the viewport (max height + scroll) so it never spills off
 *   screen when anchored to a chip near an edge.
 *
 * `onAction({target_state, expected_state, reason})` → resolves the api result
 * `{ success, code?, current_state?, error? }` (the page owns optimistic+revert).
 *
 * Death-record flag (FE-5, D43): `bed.death_recorded` (bool, absent ⇒ false,
 * S8) renders a text LABEL beside a red dot — same rule as the tile, never
 * colour alone.
 */
export default function BedPopover({ bed, canManage = false, onAction, onClose }) {
  const [pendingBlock, setPendingBlock] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null); // { kind: 'conflict'|'error', text }

  const meta = BED_STATE_META[bed.state] || BED_STATE_META.VACANT;
  const actions = canManage ? (MANUAL_TRANSITIONS[bed.state] || []) : [];

  const submit = async (target) => {
    if (busy) return;
    if (target === 'BLOCKED' && !reason.trim()) return; // reason-required guard
    setBusy(true);
    setToast(null);
    const res = await onAction?.({
      target_state: target,
      expected_state: bed.state,
      reason: target === 'BLOCKED' ? reason.trim() : undefined,
    });
    setBusy(false);
    if (res?.success) { onClose?.(); return; }
    if (res?.code === 'BED_STATE_CONFLICT') {
      setToast({
        kind: 'conflict',
        text: `This bed just changed to ${res.current_state ? labelFor(res.current_state) : 'a different state'}. Check the updated board and try again.`,
      });
    } else {
      setToast({ kind: 'error', text: res?.error || 'Could not change the bed state.' });
    }
    setPendingBlock(false);
    setReason('');
  };

  const detail = (k, v) => v != null && v !== '' && (
    <div className="flex justify-between gap-3 text-[12px]">
      <span className="text-slate-500 dark:text-slate-400">{k}</span>
      <span className="text-right font-medium text-slate-800 dark:text-slate-100">{v}</span>
    </div>
  );

  return (
    <div
      role="dialog"
      aria-label={`Bed ${bed.label}`}
      data-testid="bed-popover"
      onClick={(e) => e.stopPropagation()}
      className="max-h-[80vh] w-[264px] max-w-[92vw] overflow-auto rounded-xl border border-slate-200 bg-white p-3 shadow-xl dark:border-slate-700 dark:bg-slate-900"
    >
      <div className="mb-2 flex items-center gap-2">
        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: meta.dot }} aria-hidden="true" />
        <b className="text-sm text-slate-900 dark:text-slate-50">{bed.label}</b>
        <span className="text-xs text-slate-500 dark:text-slate-400">{meta.label}</span>
      </div>

      {/* [NEEDS-PO] copy — "Deceased" flag label in the bed popover. */}
      {bed.death_recorded && (
        <div
          className="mb-2 flex items-center gap-1.5 rounded-md border border-red-300 bg-red-50 px-2 py-1 text-[11.5px] font-bold text-red-800 dark:border-red-700 dark:bg-red-900/30 dark:text-red-200"
          data-testid="popover-death-flag"
        >
          <span className="inline-block h-2 w-2 rounded-full bg-red-600" aria-hidden="true" />
          Deceased
        </div>
      )}

      <div className="mb-2 space-y-0.5">
        {detail('Patient', bed.state === 'OCCUPIED' ? bed.patient_name : null)}
        {detail('IP number', bed.state === 'OCCUPIED' ? bed.ip_number : null)}
        {detail('Category', bed.bed_category_code)}
        {detail('Reason', bed.state === 'BLOCKED' ? bed.blocked_reason : null)}
      </div>

      {actions.length > 0 && (
        <div className="flex flex-col gap-1.5 border-t border-slate-100 pt-2 dark:border-slate-800">
          {actions.map((a) =>
            a.to === 'BLOCKED' ? (
              <button
                key="BLOCKED"
                type="button"
                data-testid="action-BLOCKED"
                onClick={() => setPendingBlock(true)}
                className="rounded-md border border-rose-300 bg-rose-50 px-2.5 py-1.5 text-left text-[12px] font-medium text-rose-800 hover:bg-rose-100 dark:border-rose-700 dark:bg-rose-900/40 dark:text-rose-100"
              >
                {a.label}
              </button>
            ) : (
              <button
                key={a.to}
                type="button"
                data-testid={`action-${a.to}`}
                disabled={busy}
                onClick={() => submit(a.to)}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-left text-[12px] font-medium text-slate-800 hover:bg-slate-100 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                {a.label}
              </button>
            ),
          )}

          {pendingBlock && (
            <div className="mt-1 space-y-1.5">
              <textarea
                data-testid="block-reason"
                value={reason}
                maxLength={200}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason for blocking (required)"
                className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-[12px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              />
              <button
                type="button"
                data-testid="confirm-block"
                disabled={busy || !reason.trim()}
                onClick={() => submit('BLOCKED')}
                className="w-full rounded-md bg-rose-600 px-2.5 py-1.5 text-[12px] font-semibold text-white disabled:opacity-60"
              >
                Confirm block
              </button>
            </div>
          )}
        </div>
      )}

      {canManage && actions.length === 0 && (
        <div
          data-testid="popover-note"
          className="border-t border-slate-100 pt-2 text-[11px] text-slate-500 dark:border-slate-800 dark:text-slate-400"
        >
          {bed.state === 'OCCUPIED'
            ? 'Occupied — this bed frees up when the patient is discharged or transferred (see Manage Admission).'
            : bed.state === 'RESERVED'
              ? 'Reserved — released from Manage Admission.'
              : 'No actions available for this bed.'}
        </div>
      )}

      {!canManage && (
        <div
          data-testid="popover-note"
          className="border-t border-slate-100 pt-2 text-[11px] text-slate-500 dark:border-slate-800 dark:text-slate-400"
        >
          View only — you do not have permission to change bed states.
        </div>
      )}

      {toast && (
        <div
          data-testid="bed-popover-toast"
          className={`mt-2 rounded-md px-2 py-1.5 text-[11px] font-medium ${
            toast.kind === 'conflict'
              ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'
              : 'bg-rose-100 text-rose-900 dark:bg-rose-900/40 dark:text-rose-100'
          }`}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}
